/**
 * Where a bookmark from the old site should end up.
 *
 * v1 lived at the same domain, so every bookmark a customer saved — `/dashboard` above all — now lands on
 * the v2 single-page app, which had no route for any of them and no catch-all. React Router matched
 * nothing and rendered nothing, which is the "black screen" customers reported: not an error page, just
 * an empty document with no indication anything had moved.
 *
 * The v1 route table (`app-routing.module.ts`) is a known, closed list, so the equivalents are worth
 * naming rather than sending everyone to the root and letting them hunt. Anything not listed still gets
 * the page, just without a specific suggestion.
 */

/** v1 path (no leading slash, lower-cased) -> the v2 route that replaced it. */
const EQUIVALENTS: Record<string, string> = {
  dashboard: '/app',
  transactions: '/app/transactions',
  capture: '/app/capture',
  location: '/app/devices',
  status: '/app/help/device',
  help: '/app/help',
  settings: '/app/profile',
  changesettings: '/app/profile',
  registration: '/app/profile',
  subscription: '/app/subscriptions',
  invoices: '/app/subscriptions',
  orders: '/app/orders',
  privacy: '/legal/privacy',
  terms: '/legal/terms',
  'return-policy': '/legal/returns',
};

/** What to call the destination, for a link a customer reads before clicking. */
const LABELS: Record<string, string> = {
  '/app': 'your dashboard',
  '/app/transactions': 'your transactions',
  '/app/capture': 'new transaction',
  '/app/devices': 'your scanners',
  '/app/help/device': 'Device status',
  '/app/help': 'Help',
  '/app/profile': 'your profile',
  '/app/subscriptions': 'subscriptions & billing',
  '/app/orders': 'your orders',
  '/legal/privacy': 'the Privacy Policy',
  '/legal/terms': 'the Terms of Use',
  '/legal/returns': 'the Return Policy',
};

export interface LegacyDestination {
  /** The v2 route that replaced this one, when we know it. */
  path?: string;
  /** Human label for that route. */
  label?: string;
}

/**
 * The v2 equivalent of a v1 path, if there is one.
 *
 * Only the FIRST segment is matched: v1's `capture/:id` and `subscription/:action` carried parameters
 * whose values mean nothing in v2 (a v1 capture id is not a v2 transaction), so they resolve to the
 * section rather than being carried across and 404ing one level deeper.
 */
export function legacyDestination(pathname: string): LegacyDestination {
  const first = pathname.replace(/^\/+/, '').split('/')[0]?.toLowerCase() ?? '';
  if (!first) return {};
  const path = EQUIVALENTS[first];
  if (!path) return {};
  const label = LABELS[path];
  return { path, ...(label ? { label } : {}) };
}
